/**
 * EMAIL SIGNATURE BLOCK — the single renderer for a per-site email signature.
 *
 * WHAT THIS IS
 *   Offices configure a signature (logo, practice name, phone, address, website, social /
 *   custom links) under Settings -> Email. It is stored as ONE `teamio.settings` row,
 *   Property `EMAIL_SIGNATURE`, whose PropertyValue is ASCII-safe JSON:
 *
 *     { version: 1, enabled: false,
 *       fields: { showLogo, logoUrl, practiceName, phone, address1, address2, city,
 *                 state, zip, website, tagline },
 *       links: [ { label, value } ],
 *       html: "<rendered html>", text: "rendered plain text", updatedAt: "ISO" }
 *
 *   THE FRONTEND IS THE ONLY RENDERER. The HTML/text are produced HERE, at save time, and
 *   every backend consumer (teamio-utility's SendGrid path, tx-presenter-service's three
 *   patient emails, campaign-service's merge tags) reads the stored `html` / `text`
 *   VERBATIM. Same precedent as campaigns freezing their HTML at creation. That means:
 *     - no cross-repo shared renderer, no lambda-layer change;
 *     - a consumer treats the blob as configured only when `enabled === true` AND `html`
 *       is a non-empty string.
 *
 * ⛔ ONE TWIN (since 2026-10-01): teamio-admin `lib/email-signature-render.js` is a mechanical
 *   CommonJS copy of everything below the `LOGO_BOX` line, used by onboarding ("Load reminder
 *   templates" seeds an enabled signature from the practice profile). After changing ANY code
 *   below, re-copy it there — teamio-admin `tests/email-signature-render-twin.test.js` fails until
 *   the twin's body equals this file's body (minus `export`). Edits to THIS header are safe.
 *
 * WHY THIS FILE HAS NO IMPORTS
 *   `cypress/node-tests/email-signature-render.test.js` LOADS AND EXECUTES this exact file
 *   (stripping the `export` keywords so Node can evaluate it) rather than mirroring it —
 *   the same technique as `src/utils/phiMask.js`. A mirror can only prove the design; this
 *   proves the shipped code. Keep it import-free or that test stops testing reality.
 *
 * TWO INVARIANTS THIS FILE OWES THE UI
 *   1. ENCODING. `PropertyValue` is latin1 (the Smile Bar silent-truncation precedent), so
 *      the rendered HTML is pure ASCII — every char above 126 becomes a numeric entity —
 *      and the blob is stringified with backslash-u escapes. Nothing non-ASCII ever reaches
 *      the column, in either half.
 *   2. SAVE -> FIRE AGREEMENT. `isRenderableSignatureConfig()` is the settings screen's
 *      save gate, and it MUST accept exactly the configs that render something. Both are
 *      derived from one internal `buildSignatureParts()`, so they cannot disagree — the
 *      Kanban tag-alert lesson (a config the UI happily saved could never fire) applies
 *      directly here: an enabled-but-empty signature would append nothing while the screen
 *      said "saved".
 *
 * SECURITY
 *   Every field is HTML-escaped, and only http(s) URLs ever become an `href` or `src`.
 *   `javascript:` / `data:` / protocol-relative values are dropped, never emitted.
 */

/** Bounding box for the logo, identical to wiki-service `lib/email-logo-markup.js` LOGO_BOX. */
export const LOGO_BOX = Object.freeze({ maxW: 200, maxH: 80 });

/** Blob schema version written by this renderer. */
export const SIGNATURE_VERSION = 1;

/** What the tagline says until an office changes it. */
export const DEFAULT_TAGLINE = 'Follow us:';

/** Teal accent (NOT the iris `--teal-*` alias — see the design-system note in CLAUDE.md). */
const LINK_COLOR = '#0f7259';

const BASE_LINE = 'margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;'
  + 'font-size:13px;line-height:1.5;color:#333333;';
const NAME_LINE = 'margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;'
  + 'font-size:14px;line-height:1.5;color:#1a1a1a;font-weight:bold;';
const ANCHOR_STYLE = `color:${LINK_COLOR};text-decoration:underline;`;

/** The text fields, in the order they render. Also the shape the settings form binds to. */
export const SIGNATURE_TEXT_FIELDS = Object.freeze([
  'practiceName', 'phone', 'address1', 'address2', 'city', 'state', 'zip', 'website', 'tagline',
]);

/** A blank config the settings screen can start from (and reset to). */
export function emptySignatureFields() {
  return {
    showLogo: true,
    logoUrl: '',
    practiceName: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    tagline: DEFAULT_TAGLINE,
  };
}

/* -----------------------------------------------------------------------------
 * Escaping
 * -------------------------------------------------------------------------- */

function str(value) {
  return value == null ? '' : String(value);
}

function trimmed(value) {
  return str(value).trim();
}

/** The five HTML-significant characters. `'` is escaped too so a value is safe in attributes. */
export function escapeHtml(value) {
  return str(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Every character outside printable ASCII becomes a numeric entity. Iterating with for..of
 * walks CODE POINTS, so an astral char (emoji) yields one correct `&#128512;` rather than
 * two broken surrogate entities.
 */
export function toAsciiEntities(value) {
  let out = '';
  for (const ch of str(value)) {
    const cp = ch.codePointAt(0);
    out += (cp < 32 || cp > 126) ? `&#${cp};` : ch;
  }
  return out;
}

/** Escape + ASCII-fold. Everything a field contributes to the HTML goes through this. */
function esc(value) {
  return toAsciiEntities(escapeHtml(value));
}

/** True when `s` holds any C0 control character or DEL. */
function hasControlChars(s) {
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code < 32 || code === 127) return true;
  }
  return false;
}

/* -----------------------------------------------------------------------------
 * URLs — http(s) only, always
 * -------------------------------------------------------------------------- */

// A domain typed without a scheme: at least one dot, an alphabetic TLD, optional path.
// Deliberately rejects anything containing ':' so `javascript:...` / `data:...` can never
// reach the bare-domain branch, and anything starting with '/' so `//evil.com` cannot either.
const BARE_DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:[/?#][^\s]*)?$/i;

/**
 * `value` as a safe absolute URL, or '' when it is anything else.
 * ONLY http:// and https:// survive — never javascript:, data:, mailto:, file: or a
 * protocol-relative //host.
 */
export function sanitizeSignatureUrl(value) {
  const raw = trimmed(value);
  if (!raw) return '';
  if (/[\s<>"']/.test(raw)) return '';        // whitespace/quotes ⇒ not a single URL
  if (hasControlChars(raw)) return '';        // "java\0script:" must never reach the test below
  if (!/^https?:\/\//i.test(raw)) return '';
  if (raw.replace(/^https?:\/\//i, '') === '') return '';   // "https://" and nothing else
  return raw;
}

/**
 * The website field is the one place a bare domain is accepted — practices type
 * "www.example.com", not "https://www.example.com". Anything that is neither a valid
 * http(s) URL nor a bare domain is dropped entirely (never rendered as text: a website
 * line that isn't a link is noise, and rendering it would break the save-gate agreement).
 */
export function normalizeWebsiteUrl(value) {
  const direct = sanitizeSignatureUrl(value);
  if (direct) return direct;
  const raw = trimmed(value);
  if (!raw || !BARE_DOMAIN_RE.test(raw)) return '';
  return `https://${raw}`;
}

/** Display form of a URL: scheme and any trailing slash removed. Cosmetic only. */
export function prettyUrl(url) {
  return trimmed(url).replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

/* -----------------------------------------------------------------------------
 * Logo bounding — copied from wiki-service lib/email-logo-markup.js
 * -------------------------------------------------------------------------- */

/**
 * Scale DOWN only, preserving aspect ratio, to fit LOGO_BOX. Null for unusable input.
 * Same maths as email-logo-markup.js `fitBox` — a 118x97 logo stays 118x97.
 */
export function fitLogoBox(dims, box) {
  const b = box || LOGO_BOX;
  const w = dims && Number(dims.width);
  const h = dims && Number(dims.height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  const scale = Math.min(1, b.maxW / w, b.maxH / h);
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/**
 * The <img>. Outlook desktop (Word rendering engine) IGNORES CSS max-width/max-height
 * outright, so a CSS-only cap is not a cap in email — the only bound it honours is explicit
 * width/height ATTRIBUTES, which need the image's real pixel size. When the caller knows
 * the natural size (the settings screen reads naturalWidth/naturalHeight off its live
 * preview <img> on @load) we emit scaled attributes AND the CSS cap; when it doesn't, we
 * fall back to the CSS cap alone, exactly like wiki-service does for an unreadable header.
 */
function logoImgHtml(src, dims) {
  const cap = `max-width:${LOGO_BOX.maxW}px;max-height:${LOGO_BOX.maxH}px;`;
  const tail = 'border:0;outline:none;text-decoration:none;';
  const fit = fitLogoBox(dims);
  const safeSrc = esc(src);
  if (fit) {
    return `<img src="${safeSrc}" alt="Practice logo" width="${fit.width}" height="${fit.height}"`
      + ` style="display:block;width:${fit.width}px;height:${fit.height}px;${cap}${tail}">`;
  }
  return `<img src="${safeSrc}" alt="Practice logo"`
    + ` style="display:block;width:auto;height:auto;${cap}${tail}">`;
}

/* -----------------------------------------------------------------------------
 * The one place that decides what a config renders
 * -------------------------------------------------------------------------- */

function readFields(config) {
  if (!config || typeof config !== 'object') return {};
  // Accept the whole blob ({ fields, links }) or a bare fields object — the settings screen
  // holds one shape, a consumer re-rendering a stored blob holds the other.
  const f = config.fields;
  if (f && typeof f === 'object') return f;
  return config;
}

function readLinks(config) {
  if (!config || typeof config !== 'object') return [];
  return Array.isArray(config.links) ? config.links : [];
}

/**
 * Everything renderable in `config`, resolved once. `renderEmailSignature` turns this into
 * HTML/text and `isRenderableSignatureConfig` just asks whether it is empty — which is why
 * the save gate and the renderer can never disagree.
 */
function buildSignatureParts(config, options) {
  const f = readFields(config);
  const opts = options || {};

  const logo = (f.showLogo !== false) ? sanitizeSignatureUrl(f.logoUrl) : '';

  const lines = [];
  const push = (text, kind) => {
    const t = trimmed(text);
    if (t) lines.push({ text: t, kind: kind || 'plain', href: '' });
  };

  push(f.practiceName, 'name');
  push(f.phone, 'plain');
  push(f.address1, 'plain');
  push(f.address2, 'plain');

  // "City, ST Zip" — every part optional, and the comma only appears when a city does.
  const stateZip = [trimmed(f.state), trimmed(f.zip)].filter(Boolean).join(' ');
  const city = trimmed(f.city);
  const cityLine = city ? (stateZip ? `${city}, ${stateZip}` : city) : stateZip;
  push(cityLine, 'plain');

  const website = normalizeWebsiteUrl(f.website);
  if (website) lines.push({ text: prettyUrl(website), kind: 'link', href: website });

  // A link row is renderable when it has a value; the label is optional (a bare URL row is
  // still meaningful). An http(s) value becomes an anchor, anything else — "@handle", a
  // page name — renders as escaped TEXT after "Label: ".
  const links = [];
  for (const raw of readLinks(config)) {
    if (!raw || typeof raw !== 'object') continue;
    const value = trimmed(raw.value);
    if (!value) continue;
    links.push({ label: trimmed(raw.label), value, href: sanitizeSignatureUrl(value) });
  }

  // The tagline introduces the links, so it renders only when there are links to introduce.
  // On its own, "Follow us:" is not a signature — and treating it as renderable would let
  // the save gate accept a config whose HTML is a single dangling colon.
  const tagline = links.length ? trimmed(f.tagline) : '';

  return { logo, lines, links, tagline, logoDims: opts.logoDims || null };
}

function hasContent(parts) {
  return !!(parts.logo || parts.lines.length || parts.links.length);
}

/* -----------------------------------------------------------------------------
 * Public API
 * -------------------------------------------------------------------------- */

/**
 * The save gate for the Settings -> Email signature card: does this config render anything
 * at all? Refuse to store an enabled config for which this is false — storing one would
 * report success for a signature that appends nothing to any email.
 *
 * @param {object} config  the blob (or a bare fields object)
 * @returns {boolean}
 */
export function isRenderableSignatureConfig(config) {
  return hasContent(buildSignatureParts(config));
}

/**
 * Render `config` to email-client-safe HTML and its plain-text twin.
 *
 * INLINE STYLES ONLY — email clients strip <style> blocks and CSS classes, so every rule
 * is repeated on the element that needs it (Outlook also resets fonts inside a bare <div>,
 * hence the font declaration on each line rather than only on the wrapper).
 *
 * @param {object} config              blob or fields object
 * @param {{logoDims?:{width:number,height:number}}} [options]
 *        `logoDims` is the logo's NATURAL pixel size when the caller knows it (the settings
 *        screen reads it off its preview <img>). Supplied ⇒ Outlook-proof width/height
 *        attributes; omitted ⇒ CSS-only cap. Deliberately an option rather than a blob
 *        field: the stored blob shape is a cross-repo contract and must not grow keys.
 * @returns {{html:string, text:string}}  both '' when nothing is renderable
 */
export function renderEmailSignature(config, options) {
  const parts = buildSignatureParts(config, options);
  if (!hasContent(parts)) return { html: '', text: '' };

  const htmlRows = [];
  const textRows = [];

  if (parts.logo) {
    htmlRows.push(`<div style="margin:0 0 8px 0;">${logoImgHtml(parts.logo, parts.logoDims)}</div>`);
  }

  for (const line of parts.lines) {
    const style = line.kind === 'name' ? NAME_LINE : BASE_LINE;
    if (line.kind === 'link' && line.href) {
      htmlRows.push(`<div style="${style}"><a href="${esc(line.href)}" style="${ANCHOR_STYLE}">${esc(line.text)}</a></div>`);
      textRows.push(line.href);
    } else {
      htmlRows.push(`<div style="${style}">${esc(line.text)}</div>`);
      textRows.push(line.text);
    }
  }

  if (parts.links.length) {
    // A zero-height spacer rather than a margin: Outlook drops margins on divs.
    if (htmlRows.length) {
      htmlRows.push('<div style="font-size:0;line-height:0;height:10px;">&nbsp;</div>');
      textRows.push('');
    }
    if (parts.tagline) {
      htmlRows.push(`<div style="${BASE_LINE}">${esc(parts.tagline)}</div>`);
      textRows.push(parts.tagline);
    }
    for (const link of parts.links) {
      const prefix = link.label ? `${esc(link.label)}: ` : '';
      const textPrefix = link.label ? `${link.label}: ` : '';
      if (link.href) {
        htmlRows.push(`<div style="${BASE_LINE}">${prefix}<a href="${esc(link.href)}" style="${ANCHOR_STYLE}">${esc(prettyUrl(link.href))}</a></div>`);
        textRows.push(`${textPrefix}${link.href}`);
      } else {
        htmlRows.push(`<div style="${BASE_LINE}">${prefix}${esc(link.value)}</div>`);
        textRows.push(`${textPrefix}${link.value}`);
      }
    }
  }

  const html = `<div style="${BASE_LINE}text-align:left;">${htmlRows.join('')}</div>`;
  return { html, text: textRows.join('\n') };
}

/**
 * JSON for `teamio.settings.PropertyValue`, which is a latin1 column: every non-ASCII
 * character is escaped as a backslash-u sequence so the row can never be silently mangled
 * on the way in. JSON.stringify already escapes control characters below 0x20; this walks
 * the result and folds everything above 126 (a character class of literal escapes is what
 * this deliberately avoids — see the note on hasControlChars).
 *
 * @param {object} blob
 * @returns {string} pure-ASCII JSON
 */
export function asciiSafeStringifySignature(blob) {
  const json = JSON.stringify(blob);
  if (typeof json !== 'string') return json;
  let out = '';
  for (let i = 0; i < json.length; i += 1) {
    const code = json.charCodeAt(i);
    out += code > 126 ? `\\u${code.toString(16).padStart(4, '0')}` : json.charAt(i);
  }
  return out;
}

/**
 * Parse a stored PropertyValue back into a blob. Corrupt or absent ⇒ null, never a throw:
 * a bad row must degrade to "no signature", not break the settings screen.
 *
 * @param {string|null|undefined} raw
 * @returns {object|null}
 */
export function parseSignatureSetting(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * Is a parsed blob one a backend consumer will actually append? Mirrors the contract every
 * consumer implements: `enabled === true` AND a non-empty `html`.
 *
 * @param {object|null} blob
 * @returns {boolean}
 */
export function isSignatureConfigured(blob) {
  return !!(blob && blob.enabled === true && typeof blob.html === 'string' && blob.html.trim() !== '');
}
